import React, { useState } from "react";
import * as dmsService from "../../../services/dmsService";
import * as inventoryService from "../../../services/inventoryService";
import useFinOptions from "../../Fin/useFinOptions";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { DataTable, hint, linkBtn } from "../../Fin/gl/glUi";
import { ActionHistory, DmsBadge, DocHead, Panel, Section, run } from "./dmsUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, formatDate, inputStyle, pageStyle, todayYmd, useLoad } from "../../../components/ui";

// Purchasing: purchase orders to the manufacturer and parts suppliers, goods receipts (vehicles by
// VIN, parts by quantity) and landed costs. Payables bills them against these receipts.

function NewPo({ onHide, onDone }) {
  const fin = useFinOptions(["suppliers", "locations"]);
  const cat = useDmsOptions(["variants", "colours:ext"], 0, { ext: { colour_type: "EXTERIOR" } });
  const items = useLoad(() => inventoryService.getItems({ is_active: 1 }), []);
  const [h, setH] = useState({ supplier_id: null, location_id: null, expected_date: "", supplier_reference: "" });
  const [lines, setLines] = useState([{ line_kind: "VEHICLE", variant_id: null, ext_colour_id: null, item_id: null, quantity: 1, unit_cost: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const loc = h.location_id || (fin.locations && fin.locations[0] ? fin.locations[0].value : null);
  const save = async () => {
    setBusy(true);
    const res = await run(() => dmsService.createPurchaseOrder({ ...h, location_id: loc, expected_date: h.expected_date || undefined, lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) })) }));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <Panel title="New purchase order" onHide={() => !busy && onHide()} width={980} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Save draft</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Supplier" required>
          <AppDropdown id="po-sup" value={h.supplier_id} filter options={fin.suppliers || []} onChange={(e) => setH({ ...h, supplier_id: e.value })} />
        </Field>
        <Field label="Deliver to">
          <AppDropdown id="po-loc" value={loc} options={fin.locations || []} onChange={(e) => setH({ ...h, location_id: e.value })} />
        </Field>
        <Field label="Expected">
          <input type="date" className="form-control" style={inputStyle} value={h.expected_date} onChange={(e) => setH({ ...h, expected_date: e.target.value })} />
        </Field>
        <Field label="Supplier reference">
          <input className="form-control" style={inputStyle} value={h.supplier_reference} onChange={(e) => setH({ ...h, supplier_reference: e.target.value })} />
        </Field>
      </div>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <select className="form-select" style={{ width: 120 }} aria-label="Kind" value={l.line_kind} onChange={(e) => set(i, { line_kind: e.target.value })}>
            <option value="VEHICLE">Vehicles</option>
            <option value="PART">Part</option>
          </select>
          <div style={{ flex: 1, minWidth: 240 }}>
            {l.line_kind === "VEHICLE" ? (
              <AppDropdown id={`po-v-${i}`} value={l.variant_id} filter options={cat.variants || []} onChange={(e) => set(i, { variant_id: e.value })} placeholder="Variant" />
            ) : (
              <AppDropdown id={`po-i-${i}`} value={l.item_id} filter options={(items.data || []).map((it) => ({ label: `${it.sku} – ${it.name}`, value: it.id }))} onChange={(e) => set(i, { item_id: e.value })} placeholder="Part" />
            )}
          </div>
          {l.line_kind === "VEHICLE" ? (
            <div style={{ width: 160 }}>
              <AppDropdown id={`po-c-${i}`} value={l.ext_colour_id} options={cat.ext || []} onChange={(e) => set(i, { ext_colour_id: e.value })} placeholder="Colour" />
            </div>
          ) : null}
          <input type="number" min={1} className="form-control" style={{ ...inputStyle, width: 90 }} aria-label="Quantity" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 140 }} aria-label="Unit cost" placeholder="Unit cost" value={l.unit_cost} onChange={(e) => set(i, { unit_cost: e.target.value })} />
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, j) => j !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, { line_kind: "VEHICLE", variant_id: null, ext_colour_id: null, item_id: null, quantity: 1, unit_cost: "" }])}>
        Add line
      </button>
    </Panel>
  );
}

function Receive({ po, onHide, onDone }) {
  const [vins, setVins] = useState({});
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [warehouseId, setWarehouseId] = useState(null);
  const hasParts = po.lines.some((l) => l.line_kind === "PART");
  const whs = useLoad(() => (hasParts ? dmsService.listWarehouses({ status: "ACTIVE" }) : Promise.resolve({ data: [] })), [hasParts]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const lines = po.lines.map((l) =>
      l.line_kind === "VEHICLE"
        ? { po_line_id: l.id, units: String(vins[l.line_no] || "").split(/[\s,]+/).filter(Boolean).map((vin) => ({ vin: vin.toUpperCase() })) }
        : { po_line_id: l.id, quantity: Number(qty[l.line_no] || 0) }
    );
    const res = await run(() => dmsService.purchaseOrderAction(po.id, "receive", { lines, receipt_date: todayYmd(), supplier_delivery_note: note, warehouse_id: warehouseId || undefined }));
    setBusy(false);
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <Panel title={`Receive against ${po.po_number}`} onHide={() => !busy && onHide()} width={760} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Post goods receipt</PrimaryButton></>}>
      <Field label="Supplier delivery note">
        <input className="form-control" style={inputStyle} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {hasParts ? (
        <Field label="Parts go into">
          <AppDropdown id="grn-wh" value={warehouseId} options={[{ label: "The default warehouse", value: null }, ...(whs.data || []).map((w) => ({ label: `${w.code} – ${w.name}`, value: w.id }))]} onChange={(e) => setWarehouseId(e.value)} />
        </Field>
      ) : null}
      {po.lines.map((l) => {
        const open = Number(l.quantity) - Number(l.qty_received);
        return (
          <div key={l.line_no} className="mb-3">
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {l.line_no}. {l.description} <span style={hint}>· {open} still to come</span>
            </div>
            {open > 0 ? (
              l.line_kind === "VEHICLE" ? (
                <textarea className="form-control" rows={2} placeholder="VINs, one per line" aria-label={`VINs for line ${l.line_no}`} value={vins[l.line_no] || ""} onChange={(e) => setVins({ ...vins, [l.line_no]: e.target.value })} />
              ) : (
                <input type="number" min={0} max={open} className="form-control" style={{ ...inputStyle, width: 140 }} aria-label={`Quantity for line ${l.line_no}`} value={qty[l.line_no] ?? ""} onChange={(e) => setQty({ ...qty, [l.line_no]: e.target.value })} />
              )
            ) : null}
          </div>
        );
      })}
      <div style={hint}>Vehicles enter stock at the order's cost; add freight or duty afterwards as a landed cost.</div>
    </Panel>
  );
}

function PoView({ id, onHide, onChanged }) {
  const { data: po, loading, error, reload } = useLoad(() => dmsService.getPurchaseOrder(id), [id]);
  const fin = useFinOptions(["suppliers"]);
  const [receiving, setReceiving] = useState(false);
  const [landed, setLanded] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doIt = (a) => run(() => dmsService.purchaseOrderAction(id, a), refresh);
  return (
    <Panel title={po ? `Purchase order ${po.po_number}` : "Purchase order"} onHide={onHide} width={980}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {po ? (
          <>
            <DocHead title="" number={po.supplier_name} status={po.status} sub={`${formatDate(po.order_date)}${po.expected_date ? ` · expected ${formatDate(po.expected_date)}` : ""} · ${po.currency} ${fmtMoney(po.total)}${po.supplier_reference ? ` · ref ${po.supplier_reference}` : ""}`}>
              <SecondaryButton onClick={() => dmsService.purchaseOrderPdf(id, `${po.po_number}.pdf`)}>PDF</SecondaryButton>
              {po.status === "DRAFT" ? <PrimaryButton onClick={() => doIt("approve")}>Approve</PrimaryButton> : null}
              {["APPROVED", "PARTIALLY_RECEIVED"].includes(po.status) ? <PrimaryButton onClick={() => setReceiving(true)}>Receive goods</PrimaryButton> : null}
              {["PARTIALLY_RECEIVED", "RECEIVED"].includes(po.status) ? <SecondaryButton onClick={() => window.confirm("Close this purchase order?") && doIt("close")}>Close</SecondaryButton> : null}
              {["DRAFT", "APPROVED"].includes(po.status) ? <SecondaryButton onClick={() => window.confirm("Cancel this purchase order?") && doIt("cancel")}>Cancel</SecondaryButton> : null}
            </DocHead>
            <Section title="Lines">
              <DataTable
                rows={po.lines}
                rowKey={(l) => l.line_no}
                columns={[
                  { label: "#", render: (l) => l.line_no },
                  { label: "Item", render: (l) => l.description },
                  { label: "Ordered", num: true, render: (l) => Number(l.quantity) },
                  { label: "Received", num: true, render: (l) => Number(l.qty_received) },
                  { label: "Billed", num: true, render: (l) => Number(l.qty_billed) },
                  { label: "Unit cost", num: true, render: (l) => fmtMoney(l.unit_cost) },
                ]}
              />
            </Section>
            <Section title="Goods receipts">
              {po.receipts.length ? (
                po.receipts.map((g) => (
                  <div key={g.grn_number} className="mb-3">
                    <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                      <b>
                        {g.grn_number} · {formatDate(g.receipt_date)} {g.supplier_delivery_note ? `· DN ${g.supplier_delivery_note}` : ""}
                      </b>
                      <button type="button" style={linkBtn} onClick={() => setLanded(g)}>
                        Add landed cost
                      </button>
                    </div>
                    {g.lines.map((l, i) => (
                      <div key={i} style={{ fontSize: 13 }}>
                        {l.vin ? `VIN ${l.vin}` : `${Number(l.quantity)} units`} · cost {fmtMoney(l.unit_cost)}
                        {Number(l.landed_cost) ? ` + landed ${fmtMoney(l.landed_cost)}` : ""} · billed {Number(l.qty_billed)}
                      </div>
                    ))}
                    {g.charges.map((c) => (
                      <div key={c.id} style={{ fontSize: 13, color: "#4B5563" }}>
                        {c.charge_type.toLowerCase()}: {c.description} {fmtMoney(c.amount)} ({c.allocation_basis.toLowerCase()}) · billed {fmtMoney(c.amount_billed)}
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div style={hint}>Nothing received yet.</div>
              )}
            </Section>
            <Section title="Action history">
              <ActionHistory actions={po.actions} />
            </Section>
          </>
        ) : null}
      </LoadState>
      {receiving && po ? <Receive po={po} onHide={() => setReceiving(false)} onDone={refresh} /> : null}
      <FormDrawer
        show={!!landed}
        title={landed ? `Landed cost on ${landed.grn_number}` : ""}
        initial={{ charge_type: "FREIGHT", allocation_basis: "VALUE" }}
        onHide={() => setLanded(null)}
        onDone={refresh}
        onSave={(clean) => dmsService.addLandedCost(landed.id, { ...clean, amount: Number(clean.amount) })}
        fields={[
          { key: "charge_type", label: "Type", type: "select", required: true, options: ["FREIGHT", "DUTY", "INSURANCE", "CLEARING", "OTHER"].map((t) => ({ label: t.charAt(0) + t.slice(1).toLowerCase(), value: t })) },
          { key: "description", label: "Description", required: true, max: 240 },
          { key: "amount", label: "Amount (ledger currency)", type: "number", step: "any", min: 0, required: true },
          { key: "allocation_basis", label: "Spread by", type: "select", required: true, options: [{ label: "Value", value: "VALUE" }, { label: "Quantity", value: "QUANTITY" }] },
          { key: "supplier_id", label: "Billed by (supplier)", type: "select", options: fin.suppliers || [] },
        ]}
      />
    </Panel>
  );
}

export default function Purchasing() {
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useLoad(() => dmsService.listPurchaseOrders({ status: status || undefined }), [status]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Purchasing" subtitle="Orders to the manufacturer and parts suppliers, goods receipts and landed costs" actions={<PrimaryButton onClick={() => setCreating(true)}>New purchase order</PrimaryButton>} />
      <div className="mb-3" style={{ width: 200 }}>
        <AppDropdown
          id="po-st"
          value={status}
          options={[
            { label: "All", value: "" },
            { label: "Draft", value: "DRAFT" },
            { label: "Approved", value: "APPROVED" },
            { label: "Part received", value: "PARTIALLY_RECEIVED" },
            { label: "Received", value: "RECEIVED" },
            { label: "Closed", value: "CLOSED" },
          ]}
          onChange={(e) => setStatus(e.value)}
        />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No purchase orders" />}
          columns={[
            { label: "PO", render: (r) => <b>{r.po_number}</b> },
            { label: "Supplier", render: (r) => r.supplier_name },
            { label: "Ordered", render: (r) => formatDate(r.order_date) },
            { label: "Expected", render: (r) => formatDate(r.expected_date) },
            { label: "Total", num: true, render: (r) => `${fmtMoney(r.total)} ${r.currency}` },
            { label: "Status", render: (r) => <DmsBadge status={r.status === "PARTIALLY_RECEIVED" ? "PARTIALLY_DELIVERED" : r.status} /> },
          ]}
        />
      </LoadState>
      {open ? <PoView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {creating ? (
        <NewPo
          onHide={() => setCreating(false)}
          onDone={(id) => {
            reload();
            setOpen(id);
          }}
        />
      ) : null}
    </div>
  );
}
